import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Centered error-state block for the Story-Output flow (Batch 12
 * Sub-phase B). Single variant-agnostic shell — illustration + title
 * + body + 1-2 CTAs. Variant-specific props are built by the factory
 * functions in `pwa/src/features/story/error-variants.ts`.
 *
 * Layout per Sönke mockup (`dreamable_ch8_share_rating_errors_player.html`,
 * frames "Error: Technical" + "Error: Limit-Block"): vertically
 * stacked, centered on the viewport, illustration ~120px, primary
 * CTA dream-gold, secondary CTA dream-navy outline.
 *
 * Workstream F.6 hybrid pattern (2026-05-07): when `errorEnvelope`
 * is provided AND carries a `user_message`, the backend wording wins
 * — it replaces the variant's `title` and the variant's `body` is
 * suppressed. When envelope is absent or `user_message` is empty,
 * the variant strings render unchanged. Mirrors the precedent from
 * the PDF click-fetch flow (Batch 12 Sub-phase A).
 */
export interface StoryErrorBlockProps {
  /** Path to a self-hosted error illustration (Sebastian-uploaded). */
  illustrationSrc: string;
  /** Alt text — describes the illustration character/scene. */
  illustrationAlt: string;
  /** Title — font-semibold, briefing-locked verbatim copy (variant fallback). */
  title: string;
  /** Body — opacity-50, em-dashes preserved verbatim. Optional under the
   *  hybrid pattern: suppressed when `errorEnvelope.user_message` is present. */
  body?: string;
  primaryCta: { label: string; onClick: () => void };
  /** Optional second CTA — used by the Limit-Block variant only. */
  secondaryCta?: { label: string; onClick: () => void };
  /** Optional backend error envelope. When `user_message` is non-empty it
   *  replaces the title and suppresses the body. Narrow shape (only the
   *  field consumed) keeps the component decoupled from the full
   *  ApiError envelope contract. */
  errorEnvelope?: { user_message?: string };
  /** Optional supplementary slot below the CTA group — used by the
   *  Error-Boundary fallback (F.5) to surface a `support@`-mailto line.
   *  Wrapped in a styled paragraph (text-xs, opacity-50, mt-4) matching
   *  the body-text treatment. */
  footer?: ReactNode;
  className?: string;
  testId?: string;
}

export function StoryErrorBlock({
  illustrationSrc,
  illustrationAlt,
  title,
  body,
  primaryCta,
  secondaryCta,
  errorEnvelope,
  footer,
  className,
  testId,
}: StoryErrorBlockProps) {
  // Hybrid resolution: backend user_message wins when non-empty.
  const userMessage = errorEnvelope?.user_message?.trim();
  const displayTitle = userMessage ? userMessage : title;
  const displayBody = userMessage ? undefined : body;

  return (
    <div
      role="alert"
      className={cn(
        'mx-auto flex w-full max-w-[420px] flex-col items-center gap-3 px-5 py-12 text-center',
        className,
      )}
      data-testid={testId}
    >
      <img
        src={illustrationSrc}
        alt={illustrationAlt}
        // Fixed dimensions prevent CLS while the asset loads (or
        // fails to load — Sebastian-uploaded assets pending).
        width={120}
        height={120}
        className="h-[120px] w-[120px] object-contain"
        draggable={false}
      />
      <p className="text-base font-semibold leading-tight text-dream-navy">{displayTitle}</p>
      {displayBody && <p className="text-xs leading-5 text-dream-navy opacity-50">{displayBody}</p>}
      <div className="mt-2 flex w-full flex-col items-stretch gap-2">
        <button
          type="button"
          onClick={primaryCta.onClick}
          className="flex w-full items-center justify-center rounded-lg bg-dream-gold px-4 py-[14px] text-xs font-semibold text-dream-navy transition-colors hover:bg-dream-gold/90"
          data-testid={testId ? `${testId}-primary-cta` : undefined}
        >
          {primaryCta.label}
        </button>
        {secondaryCta && (
          <button
            type="button"
            onClick={secondaryCta.onClick}
            className="flex w-full items-center justify-center rounded-lg border border-dream-navy bg-transparent px-4 py-[14px] text-xs font-semibold text-dream-navy transition-colors hover:bg-dream-navy/5"
            data-testid={testId ? `${testId}-secondary-cta` : undefined}
          >
            {secondaryCta.label}
          </button>
        )}
      </div>
      {footer && (
        <div
          className="mt-4 text-xs leading-5 text-dream-navy opacity-50"
          data-testid={testId ? `${testId}-footer` : undefined}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
